// Generic import
import _ from 'lodash';

// Local import
import { sectionsToTaxParams } from './Formula.js';
import { evaluate } from './Formula.js';
import { FieldTypes } from '../FieldTypes.js';
import { calcTotalVolume } from '../CalcWeight.js';
import { getDisplayRate } from '../dashboard/utils/opportunity.js';
import { getTotalRates } from '../dashboard/utils/rates.js';

function calcFieldChargeLineValue({ field, line, sections, products, transportationMethod, exchangeRates, cost, organization, buyingRates, visiting }) {
    const { quantityMultiplier: multiplier, multiplierText, fieldValueId, productsInChargeLine } = line;
    const formula = field.type === 'custom' ? null : _.get(field, `values[${fieldValueId}].formula`, null);
    const value = _.get(field, `values[${fieldValueId}].value`, null);
    const currency = _.get(field, `values[${fieldValueId}].currency`, organization.defaults?.currency);

    const sum = {};
    let showSection = false;

    if (formula) {
        showSection = true;
        try {
            let params = {};
            if (formula.indexOf('field_') > -1) {
                const fields = formula.match(/field_[0-9a-f_]+/g) || [];
                if (!sections) return { sum: {}, showSection: false };
                params = sectionsToTaxParams({ fields, sections, products, transportationMethod, targetCurrency: currency, exchangeRates, organization, buyingRates, visiting: new Set([...(visiting || []), field.id]) });
            }
            // For LCL formulas, provide tons, cbm, weightKg from the product
            const lclProduct = (productsInChargeLine || products)?.find(p => p.type === 'LCL' && p.calculations);
            if (lclProduct) {
                const totalVolume = calcTotalVolume({ product: lclProduct });
                let totalWeightKg = 0;
                for (const calc of (lclProduct.calculations || [])) {
                    totalWeightKg += (calc.weight || 0) * (calc.quantity || 1);
                }
                if ((lclProduct.kgOrLbs || 'kg') !== 'kg') totalWeightKg = totalWeightKg * 0.453592;
                const totalCbm = totalVolume.postfix === 'cft' ? totalVolume.val * 0.0283168 : totalVolume.val;
                const totalTons = totalWeightKg / 1000;
                params.tons = totalTons;
                params.cbm = totalCbm;
                params.weightKg = totalWeightKg;
            }
            sum[currency] = (sum[currency] || 0) + evaluate(formula, { x: multiplier, buying: (cost || 0) * exchangeRates[currency], ...params });
        } catch (e) {
            console.error(`Error in formula for field ${field.id} - flat`, formula, e);
            sum[currency] = (sum[currency] || 0) + 0;
        }
    } else if (value || value === 0) {
        if (!_.isNaN(parseFloat(value))) showSection = true;
        sum[currency] = (sum[currency] || 0) + ((parseFloat(value) || 0) * multiplier);
    }

    return { sum, showSection };
}

// What one supplier cost behind a charge comes to, in its own currency ({ USD: 3800 }). The cost
// is one of a charge line's buyingRates, as getChargeLines gives them: only the costs that apply
// to the line's product.
function calcCostValue({ cost: r, transportationMethod, exchangeRates, lockedExchangeRates, organization }) {
    if (r.field?.type === 'string') return {};
    // The rate behind a cost can be taken away while the screen is still holding it: the
    // cost centre unlinks and deletes a card while a rate settings dialog sits open on the
    // same rate, and the copy being rendered still lists the cost. Reading the sections off
    // a rate that is no longer there threw, and took the whole quote down rather than the
    // one charge. A cost whose rate has gone contributes nothing, which is what deleting it
    // was asking for.
    if (!r.rate?.offer?.sections || !r.rateProduct) return {};
    // No need to consider the multiplier here since it is considered in calcFieldValue
    return calcFieldValue({
        field: r.field,
        products: [{ ...r.product, id: r.rateProduct.id }],
        transportationMethod,
        sections: r.rate.offer.sections,
        cost: 0,
        exchangeRates,
        lockedExchangeRates,
        organization,
        forceLine: { ...r, quantityMultiplier: r.multiplier, fieldValueId: r.field.type === 'flat' ? 'flat' : r.rateProduct.id }
    }).sum;
}

function calcFieldValue({ field, products, transportationMethod, sections, buyingRates, fieldBuyingRates, cost, exchangeRates = {}, lockedExchangeRates, organization, forceLine = null, visiting }) {
    const sum = {};
    let showSection = false;

    if (cost && _.isEmpty(buyingRates)) {
        console.error('[opportunity.js] calcFieldValue: cost provided but no buyingRates (cost is deprecated)');
    }

    const fieldType = FieldTypes[transportationMethod]?.find(f => f.value === field.type);
    const chargeLines = forceLine ? [forceLine] : fieldType?.getChargeLines ? fieldType.getChargeLines({ field, products, buyingRates: buyingRates || [], fieldBuyingRates: fieldBuyingRates || [], organization }) : [];

    chargeLines.forEach(line => {

        const rates = line.buyingRates || [];
        let costSum = 0;
        rates.forEach(r => {
            costSum += _.sum(_.map(calcCostValue({ cost: r, transportationMethod, exchangeRates, lockedExchangeRates, organization }), (val, currency) => val / exchangeRates[currency]));
        });

        const _sum = calcFieldChargeLineValue({ field, line, sections, products, transportationMethod, exchangeRates, cost: costSum, organization, buyingRates, visiting });
        showSection = showSection || _sum.showSection;
        _.forEach(_sum.sum, (value, currency) => sum[currency] = (sum[currency] || 0) + value);
    });

    if (field.displayCurrency) {
        const newSum = {
            [field.displayCurrency]: _.sum(_.map(sum, (value, currency) => {
                const rate = getDisplayRate({
                    sourceCurrency: currency,
                    displayCurrency: field.displayCurrency,
                    displayCurrencySource: field.displayCurrencySource,
                    lockedExchangeRates,
                    liveRates: exchangeRates,
                    fixedRates: organization?.exchangeRates
                });
                return value * rate;
            }))
        }
        return { sum: newSum, showSection };
    }

    return { sum, showSection };
}

function calcSectionOfferSum({ sectionOffer, displayCurrency, displayCurrencySource, products, transportationMethod, sections, buyingRates, exchangeRates = {}, lockedExchangeRates, organization }) {

    const sum = {};
    let showSection = false;

    _.forEach(sectionOffer?.fields, field => {
        const _sum = calcFieldValue({ field, products, transportationMethod, sections, buyingRates, fieldBuyingRates: field.buyingRates, exchangeRates, lockedExchangeRates, organization });
        if (!_sum) return null;
        showSection = showSection || _sum.showSection;
        _.forEach(_sum.sum, (value, currency) => sum[currency] = (sum[currency] || 0) + value);
    });

    if (displayCurrency) {
        const newSum = {
            [displayCurrency]: _.sum(_.map(sum, (value, currency) => {
                const rate = getDisplayRate({
                    sourceCurrency: currency,
                    displayCurrency,
                    displayCurrencySource,
                    lockedExchangeRates,
                    liveRates: exchangeRates,
                    fixedRates: organization.exchangeRates
                });
                return value * rate;
            }))
        }
        return { sum: newSum, showSection }
    }

    return { sum, showSection };
}

function calcSectionSum({ section, products, transportationMethod, buyingRates, selected = [], sections, exchangeRates, lockedExchangeRates, organization, preCalcs = {} }) {
    let min = { normalized: Number.MAX_VALUE };
    let max = { normalized: -1 };
    let showSection = false;

    const selectedOffer = section.offers.find(o => selected.includes(o.id));
    if (selectedOffer) {
        const { sum, showSection: _showSection } = preCalcs[selectedOffer.id] || calcSectionOfferSum({ 
            sectionOffer: selectedOffer, displayCurrency: section.displayCurrency, displayCurrencySource: section.displayCurrencySource, 
            buyingRates, products, transportationMethod, sections, exchangeRates, lockedExchangeRates, organization });
        showSection = showSection || _showSection; 
        const normalized = _.sumBy(_.toPairs(sum), ([currency, val]) => val / exchangeRates[currency]);
        return {
            min: _.cloneDeep({ id: selectedOffer.id, sum, normalized }),
            max: _.cloneDeep({ id: selectedOffer.id, sum, normalized }),
            showSection,
        }
    }

    section.offers.forEach(offer => {
        const { sum, showSection: _showSection } = preCalcs[offer.id] || calcSectionOfferSum({ 
            sectionOffer: offer, displayCurrency: section.displayCurrency, displayCurrencySource: section.displayCurrencySource, 
            buyingRates, products, sections, transportationMethod, exchangeRates, lockedExchangeRates, organization });
        const normalized = _.sumBy(_.toPairs(sum), ([currency, val]) => val / exchangeRates[currency]);

        showSection = showSection || _showSection; 

        if (min.normalized > normalized) {
            min = _.cloneDeep({ normalized, sum, id: offer.id });
        }
        if (max.normalized < normalized) {
            max = _.cloneDeep({ normalized, sum, id: offer.id });
        }
    });

    // The sentinels above exist to be beaten by the first real offer. A section no offer
    // ever beat - it has none, or none whose total could be worked out - would otherwise
    // hand MAX_VALUE back as though it were money, and two of those added together overflow
    // to Infinity, which is how a summary came to report its revenue as an infinite amount.
    if (min.normalized === Number.MAX_VALUE) {
        min = { sum: {}, normalized: 0 };
    }
    if (max.normalized === -1) {
        max = _.cloneDeep(min);
    }

    return { min, max, showSection };
}

function calcOfferSum({ offer, products, transportationMethod, buyingRates, selected = [], exchangeRates, preCalcs = {}, lockedExchangeRates, organization }) {
    let min = null;
    let max = null;

    offer.sections.forEach(section => {
        const sectionSum = calcSectionSum({ section, sections: offer.sections, products, transportationMethod, buyingRates, selected, exchangeRates, preCalcs: preCalcs[section.id], lockedExchangeRates, organization });

        if (min === null) {
            min = { sum: { ...(sectionSum.min?.sum || {}) }, normalized: sectionSum.min?.normalized || 0 };
            max = { sum: { ...(sectionSum.max?.sum || {}) }, normalized: sectionSum.max?.normalized || 0 };
        } else {
            _.forEach(sectionSum.min?.sum, (val, currency) => min.sum[currency] = (min.sum[currency] || 0) + val);
            min.normalized += sectionSum.min?.normalized || 0;
            _.forEach(sectionSum.max?.sum, (val, currency) => max.sum[currency] = (max.sum[currency] || 0) + val);
            max.normalized += sectionSum.max?.normalized || 0;
        }
    });

    if (min === null) {
        min = { sum: { [organization?.defaults?.currency || 'USD']: 0 }, normalized: 0 };
    }
    if (max === null) {
        max = { sum: { [organization?.defaults?.currency || 'USD']: 0 }, normalized: 0 };
    }

    return { min, max };
}

// Deleting a quote is a soft delete: `status` flips to 'deleted' and nothing else changes, so a
// restore only has to decide which status to put back. Deletes made after the 'deleted' log action
// landed record the exact previous status on the log entry; older ones don't, so fall back to the
// send history — a quote with a 'sent-to-endcustomer' entry goes back to sent, anything else
// reopens as a plain in-progress quote (null).
function restoreStatus(opportunity) {
    const deletion = _.findLast(opportunity?.log || [], l => l.action === 'deleted');
    if (deletion && Object.prototype.hasOwnProperty.call(deletion, 'previousStatus')) {
        return deletion.previousStatus;
    }
    return (opportunity?.log || []).some(l => l.action === 'sent-to-endcustomer') ? 'sent-to-endcustomer' : null;
}

export { calcSectionOfferSum, calcSectionSum, calcOfferSum, calcFieldValue, calcFieldChargeLineValue, calcCostValue, restoreStatus }
